/**
 * Vocabulário visual do mural: rótulos, cores por categoria e o CPF mascarado.
 *
 * As classes são strings COMPLETAS de propósito — o Tailwind varre o código
 * procurando literais, então `bg-amber/10` montado por concatenação não
 * chegaria ao CSS final.
 */
import { Role } from '@/types/auth';
import type { MuralCategory, MuralStatus } from '@/types/models';

export interface CategoryMeta {
  label: string;
  /** Fundo + borda do post-it. */
  paper: string;
  /** Cor do rótulo da categoria (token já calibrado nos dois temas). */
  ink: string;
}

export const CATEGORY_META: Record<MuralCategory, CategoryMeta> = {
  receita: { label: 'Receita', paper: 'bg-amber/10 border-amber/40', ink: 'text-amber' },
  exame: { label: 'Exame', paper: 'bg-info/10 border-info/40', ink: 'text-info' },
  agenda: { label: 'Agenda', paper: 'bg-sage/10 border-sage/40', ink: 'text-sage' },
  convenio: { label: 'Convênio', paper: 'bg-terracotta/10 border-terracotta/40', ink: 'text-terracotta' },
  outro: { label: 'Outro', paper: 'bg-surface-container-high border-outline-variant/60', ink: 'text-on-surface-variant' },
};

export const STATUS_META: Record<MuralStatus, { label: string; hint: string }> = {
  anotado: {
    label: 'Anotado',
    hint: 'Entra aqui e fica até alguém resolver. No fim do dia, esta coluna é a pauta.',
  },
  aguardando: {
    label: 'Aguardando',
    hint: 'A bola está com terceiro — paciente, convênio, laboratório.',
  },
  resolvido: {
    label: 'Resolvido',
    hint: 'Dá pra puxar de volta o dia inteiro; some do mural só na virada.',
  },
};

/**
 * CPF mascarado: mostra só os três dígitos do meio — o suficiente pra
 * desempatar dois "Maria Silva" sem estampar o documento inteiro num quadro
 * que fica aberto no balcão. A busca continua aceitando o CPF completo.
 */
export function maskedPatientNumber(patientNumber?: string): string | null {
  if (!patientNumber) return null;
  if (!/^\d{11}$/.test(patientNumber)) return null; // 'P-000123' → sem CPF
  return `•••.${patientNumber.slice(3, 6)}.•••-••`;
}

/** "hoje" / "1 dia" / "12 dias". */
export function ageLabel(days: number): string {
  if (days <= 0) return 'hoje';
  return days === 1 ? '1 dia' : `${days} dias`;
}

/** Cargo por extenso, carimbado em quem escreve no post-it. */
export const MURAL_ROLE_LABEL: Partial<Record<Role, string>> = {
  [Role.ADMIN]: 'Administrador',
  [Role.MEDICO]: 'Médico',
  [Role.SECRETARIA]: 'Secretária',
  [Role.RECEPCIONISTA]: 'Recepcionista',
};

/** "seg 17:56" / "17:56" (hoje). */
export function shortStamp(ms: number, today: string): string {
  const tz = 'America/Sao_Paulo';
  const hm = new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: tz });
  const day = new Date(ms).toLocaleDateString('sv-SE', { timeZone: tz });
  if (day === today) return hm;
  const wd = new Date(ms).toLocaleDateString('pt-BR', { weekday: 'short', timeZone: tz }).replace('.', '');
  return `${wd} ${hm}`;
}
